import React, { useEffect, useRef, useState } from "react";
import { Routes, Route, useLocation, NavLink, useNavigate, Navigate } from "react-router-dom";
import { doc, onSnapshot, collection, getDocs, setDoc } from "firebase/firestore";
import { db } from "./utils/firebase";
import ProjectNav from "./components/ProjectNav";
import ContentList from "./components/ContentList";
import Editor from "./components/Editor";
import TipEditor from "./components/TipEditor";
import GeneralSettings from "./components/GeneralSettings";
import AppSettings from "./components/AppSettings";
import PremiumFiles from "./components/PremiumFiles";
import AiGenerationSettings from "./components/AiGenerationSettings";
import FreeAccessSettings from "./components/FreeAccessSettings";
import ContourSwitch from "./components/ContourSwitch";
import { useAuth } from "../../context/AuthContext";
import { logActivity } from "./activityLog";
import { fnUrl } from "../../utils/fnUrl";
import { toast } from "react-toastify";
import "../../styles/cms.css";

const CONTENT_TYPES = [
  { id: "insights", label: "Insights" },
  { id: "premium", label: "Premium" },
  { id: "app-settings", label: "App Settings" },
];

const ContentTypeTabs = () => {
  const location = useLocation();
  const { isDev, user, firebaseUser } = useAuth();
  const pathParts = location.pathname.replace(/^\/cms\/?/, "").split("/");
  const project = pathParts[0] || null;
  const [deploying, setDeploying] = useState(false);
  const [showEnvMenu, setShowEnvMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const mountedAtRef = useRef<Date>(new Date());

  useEffect(() => {
    if (!project) return;
    const unsub = onSnapshot(doc(db, "content", project, "_config", "deploy_status"), (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      const completedAt: Date | null = data.completedAt?.toDate?.() || null;
      if (!completedAt || completedAt <= mountedAtRef.current) return;
      const { status, env } = data;
      const envLabel = env === "prod" ? "Production" : "Stage";
      if (status === "success") {
        toast.success(`${envLabel} deployed!`);
        logActivity(project, { action: "deploy_complete", detail: env, user: "ci" });
      } else {
        toast.error(`${envLabel} deploy failed`);
      }
    });
    return () => unsub();
  }, [project]);

  if (!project) return null;

  const triggerDeploy = async (env: "dev" | "prod") => {
    setShowEnvMenu(false);
    if (deploying) return;
    const label = env === "prod" ? "PRODUCTION" : "Stage";
    if (!confirm(`Deploy ${project} to ${label}?`)) return;
    setDeploying(true);
    try {
      const base = fnUrl("cmsDeploy");
      const resp = await fetch(base, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ project, env, user: user?.email || "unknown" }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.error || `HTTP ${resp.status}`);
      toast.success(`Deploy started (${env})`);
      logActivity(project, { action: "deploy", detail: env, user: user?.email || firebaseUser?.email || "unknown", photoURL: user?.photoURL || firebaseUser?.photoURL || undefined }).catch(() => {});
    } catch (err: any) {
      console.error("[CmsPage] Deploy error:", err);
      toast.error(`Deploy failed: ${err.message}`);
    } finally {
      setDeploying(false);
    }
  };

  return (
    <div className="cms__tabs">
      {/* FIRST IN THE BAR, BEFORE THE TABS. What you are editing is read before
          where you are editing it — and before Deploy, which is the other
          control in this row that can reach buyers. */}
      <ContourSwitch project={project} />
      {CONTENT_TYPES.map((t) => (
        <NavLink
          key={t.id}
          to={`/cms/${project}/${t.id}`}
          className={({ isActive }) =>
            `cms__tab ${isActive ? "cms__tab--active" : ""}`
          }
        >
          {t.label}
        </NavLink>
      ))}
      <NavLink
        to={`/cms/${project}/ai-generation`}
        className={({ isActive }) =>
          `cms__tab ${isActive ? "cms__tab--active" : ""}`
        }
      >
        AI Generation
      </NavLink>
      <NavLink
        to={`/cms/${project}/free-access`}
        className={({ isActive }) =>
          `cms__tab ${isActive ? "cms__tab--active" : ""}`
        }
      >
        Free access
      </NavLink>
      {isDev && (
        <NavLink
          to={`/cms/${project}/general`}
          className={({ isActive }) =>
            `cms__tab ${isActive ? "cms__tab--active" : ""}`
          }
        >
          General
        </NavLink>
      )}
      <div className="cms__deploy-wrap" ref={menuRef}>
        <button
          className="cms__deploy-btn"
          onClick={() => setShowEnvMenu((v) => !v)}
          disabled={deploying}
        >
          {deploying ? "Deploying..." : "Deploy ▾"}
        </button>
        {showEnvMenu && (
          <div className="cms__deploy-menu">
            <button onClick={() => triggerDeploy("dev")}>Stage</button>
            <button className="cms__deploy-menu--prod" onClick={() => triggerDeploy("prod")}>Prod</button>
          </div>
        )}
      </div>
    </div>
  );
};

const CmsPage = () => {
  const { isDev } = useAuth();
  return (
    <div className="cms">
      <ProjectNav />
      <div className="cms__main">
        <ContentTypeTabs />
        <Routes>
          <Route index element={<CmsRedirect />} />
          <Route path=":project" element={<CmsProjectWelcome />} />
          <Route path=":project/general" element={isDev ? <GeneralSettings /> : <CmsProjectWelcome />} />
          <Route path=":project/ai-generation" element={<AiGenerationSettings />} />
          <Route path=":project/free-access" element={<FreeAccessSettings />} />
          <Route path=":project/premium" element={<PremiumFiles />} />
          <Route path=":project/app-settings" element={<AppSettings />} />
          <Route path=":project/tips" element={<TipEditor />} />
          <Route path=":project/:contentType/categories" element={<ContentList />} />
          <Route path=":project/:contentType" element={<ContentList />} />
          <Route path=":project/:contentType/:docId" element={<Editor />} />
        </Routes>
      </div>
    </div>
  );
};

const CmsRedirect = () => {
  const navigate = useNavigate();
  const { hasProject } = useAuth();
  const [done, setDone] = useState(false);

  useEffect(() => {
    const go = async () => {
      try {
        const snap = await getDocs(collection(db, "content"));
        const projects: { id: string; name: string }[] = [];
        snap.forEach((doc) => {
          const d = doc.data();
          if (d.name && hasProject(doc.id)) projects.push({ id: doc.id, name: d.name });
        });
        projects.sort((a, b) => a.name.localeCompare(b.name));
        if (projects.length) {
          navigate(`/cms/${projects[0].id}/insights`, { replace: true });
          return;
        }
      } catch (err) {
        console.error("[CmsRedirect]", err);
      }
      setDone(true);
    };
    go();
  }, []);

  if (done) return <AddProjectPlaceholder />;
  return <div className="cms__loading">Loading...</div>;
};

const AddProjectPlaceholder = () => {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [creating, setCreating] = useState(false);

  const handleNameChange = (v: string) => {
    setName(v);
    setSlug(v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""));
  };

  const handleCreate = async () => {
    if (!name.trim() || !slug.trim()) return;
    setCreating(true);
    try {
      await setDoc(doc(db, "content", slug), { name: name.trim() });
      navigate(`/cms/${slug}/insights`);
    } catch (err: any) {
      console.error("[AddProject]", err);
      alert(err.message || "Failed to create project");
    } finally {
      setCreating(false);
    }
  };

  if (!showForm) {
    return (
      <div className="cms__add-project">
        <button
          className="cms__add-project-btn"
          onClick={() => setShowForm(true)}
        >
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>
        <span className="cms__add-project-label">Add Project</span>
      </div>
    );
  }

  return (
    <div className="cms__add-project">
      <div className="cms__add-project-form">
        <h3 className="cms__add-project-title">New Project</h3>
        <label className="cms__add-project-field">
          <span>Name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="Yellow Teeth"
            autoFocus
          />
        </label>
        <label className="cms__add-project-field">
          <span>Slug</span>
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="yellow-teeth"
          />
        </label>
        <div className="cms__add-project-actions">
          <button
            className="cms__add-project-cancel"
            onClick={() => setShowForm(false)}
          >
            Cancel
          </button>
          <button
            className="cms__add-project-submit"
            onClick={handleCreate}
            disabled={creating || !name.trim() || !slug.trim()}
          >
            {creating ? "Creating..." : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
};

const CmsProjectWelcome = () => (
  <div className="cms__welcome">
    <p>Select a content type above.</p>
  </div>
);

export default CmsPage;
